import { Link } from 'react-router-dom';
import type { PricingType } from '@modelcast/types';

interface Attendee {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface EventCardData {
  code: string;
  name: string;
  description: string | null;
  scheduledAt: string | null;
  endsAt: string | null;
  startedAt: string | null;
  coverImageUrl: string | null;
  hostName: string;
  hostAvatarUrl: string | null;
  modelName: string | null;
  modelAvatarUrl: string | null;
  isTutored: boolean;
  seatsTotal: number | null;
  seatsConfirmed: number;
  attendees?: Attendee[];
  // Pricing
  pricingType?: PricingType;
  ticketPrice?: number | null; // In cents
}

interface EventCardProps {
  event: EventCardData;
  onClick?: () => void;
  variant?: 'full' | 'preview';
}

export function EventCard({ event, onClick, variant = 'full' }: EventCardProps) {
  const isLive = event.startedAt !== null;
  const isPreview = variant === 'preview';

  // Format price for top-left pill
  const getPriceText = () => {
    if (event.pricingType === 'free') return 'Free';
    if (event.pricingType === 'donation') return 'Pay what you want';
    if (event.pricingType === 'fixed' && event.ticketPrice) {
      return `$${(event.ticketPrice / 100).toFixed(0)}`;
    }
    return 'Free';
  };

  // Format date/time for top-right pill (e.g., "Wed May 7 · 3pm")
  const getShortDateTime = () => {
    if (!event.scheduledAt) return null;
    const date = new Date(event.scheduledAt);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const timeStr = minute > 0 ? `${hour12}:${minute.toString().padStart(2, '0')}${ampm}` : `${hour12}${ampm}`;
    return `${weekday} ${month} ${day} · ${timeStr}`;
  };

  const CardWrapper = isPreview ? 'div' : Link;
  const cardProps = isPreview
    ? { className: "bg-white border border-line flex flex-col overflow-hidden" }
    : {
        to: `/s/${event.code}`,
        className: "bg-white border border-line flex flex-col overflow-hidden transition-all hover:border-text hover:-translate-y-0.5",
        onClick: onClick,
      };

  return (
    <CardWrapper {...(cardProps as any)}>
      {/* Card Art */}
      <div
        className="relative w-full aspect-[4/5] bg-[#d9d4c8] bg-cover bg-center"
        style={event.coverImageUrl ? { backgroundImage: `url(${event.coverImageUrl})` } : undefined}
      >
        {/* Price pill - top left */}
        <span
          className="absolute top-3.5 left-3.5 inline-flex items-center gap-2 px-2.5 py-1.5 bg-white/95 rounded-full text-[10.5px] uppercase text-text"
        >
          {isLive && (
            <span className="w-1.5 h-1.5 rounded-full bg-[#c11] shadow-[0_0_0_3px_rgba(204,17,17,0.18)] animate-pulse" />
          )}
          {isLive ? 'Live now' : getPriceText()}
        </span>

        {/* Date & Time pill - top right */}
        {event.scheduledAt && (
          <span className="absolute top-3.5 right-3.5 px-2.5 py-1.5 bg-white/95 rounded-full text-[10.5px] uppercase text-text">
            {getShortDateTime()}
          </span>
        )}

        {/* Overlay title */}
        <span className="absolute left-4 right-4 bottom-4 text-[22px] font-normal leading-[1.12] text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]">
          {event.name}
        </span>
      </div>

      {/* Card Body */}
      {(event.description || event.modelName) && (
        <div className="px-[18px] py-3.5 flex flex-col gap-1.5">
          {event.description && (
            <p className="text-[13px] text-text-dim line-clamp-2 leading-[1.45]">
              {event.description}
            </p>
          )}
          {event.modelName && (
            <span className="text-[13px] text-text">
              with <span className="font-medium">{event.modelName}</span>
            </span>
          )}
        </div>
      )}

      {/* Card Footer */}
      <div className="flex items-center gap-2 px-[18px] py-3.5 border-t border-line-soft bg-bg mt-auto">
        <span className="flex-1 italic text-[12.5px] text-text-dim">
          {event.isTutored ? 'Tutored' : 'No guest limit'}
          {event.seatsTotal && ` · ${event.seatsTotal - event.seatsConfirmed} seats left`}
        </span>
        {!isPreview && (
          <span className="text-[12.5px] text-text border-b border-line pb-px hover:border-text">
            View
          </span>
        )}
      </div>
    </CardWrapper>
  );
}
